import { WORKSPACE_FEATURES, type JoinPolicy, type WorkspaceFeature } from '@itsappy/shared';
import {
  BookIcon,
  ChatIcon,
  CompanyIcon,
  FlameIcon,
  GamesIcon,
  HomeIcon,
  MailIcon,
  MeetingsIcon,
  ReportIcon,
  SpacesIcon,
  SparkleIcon,
  TasksIcon,
} from '@/components/ui/icons';
import type { IconComponent } from '@/components/ui';

export const FEATURE_DETAILS: Record<
  WorkspaceFeature,
  { label: string; description: string; Icon: IconComponent }
> = {
  chat: { label: 'Chat', description: 'Messages, channels and calls', Icon: ChatIcon },
  meetings: { label: 'Meetings', description: 'Schedule and join meetings', Icon: MeetingsIcon },
  games: { label: 'Games', description: 'Team and solo games', Icon: GamesIcon },
  spaces: { label: 'Spaces', description: 'Virtual office and voice', Icon: SpacesIcon },
  tasks: { label: 'Tasks', description: 'Projects, task boards and automations', Icon: TasksIcon },
  home: { label: 'Home', description: 'The dashboard landing page', Icon: HomeIcon },
  assistant: {
    label: 'Itsappy AI',
    description: 'Name and feature suggestions, and space agents',
    Icon: SparkleIcon,
  },
  company: {
    label: 'Company',
    description: 'Announcements, time off, the org chart and the handbook',
    Icon: CompanyIcon,
  },
  engagement: {
    label: 'Engagement',
    description: 'Streaks, Appys, the points store and focus sessions',
    Icon: FlameIcon,
  },
  learning: {
    label: 'Learning',
    description: 'Daily micro-lessons from what social media is discussing',
    Icon: BookIcon,
  },
  insights: {
    label: 'Insights',
    description: 'Brand watch, a morning news brief and a weekly trend radar',
    Icon: ReportIcon,
  },
  mail: {
    label: 'Email hosting',
    description: 'The workspace\u2019s own mail domains and mailboxes',
    Icon: MailIcon,
  },
};

/**
 * The access shapes a workspace actually uses, named. Handing out the same
 * four toggles person by person is where administration goes wrong at twenty
 * people; a preset is one decision applied to a selection.
 */
export const ACCESS_PRESETS: { label: string; features: WorkspaceFeature[] }[] = [
  /* Mail is left out of every preset: it is sold per mailbox, and a preset
     that hands it out with one click would hand out a bill with it. Granting
     it is a deliberate, separate tick. */
  {
    label: 'Full access',
    features: WORKSPACE_FEATURES.filter((feature) => feature !== 'mail'),
  },
  {
    label: 'Full access, no Company',
    features: WORKSPACE_FEATURES.filter(
      (feature) => feature !== 'company' && feature !== 'mail',
    ),
  },
  { label: 'Chat only', features: ['chat'] },
  { label: 'Meetings only', features: ['meetings'] },
  { label: 'Chat + meetings', features: ['chat', 'meetings'] },
  { label: 'No access', features: [] },
];

export const JOIN_POLICY_DETAILS: Record<JoinPolicy, { label: string; description: string }> = {
  approval: {
    label: 'Owner approves',
    description: 'Anyone can ask to join. They wait on the People screen until you approve them.',
  },
  open: {
    label: 'Anyone can join',
    description: 'New accounts land straight in the workspace. Best behind an email domain rule.',
  },
  'invite-only': {
    label: 'Invitation only',
    description: 'Requests are turned away. People get in through an invite link or a new account you create.',
  },
};

export function sameFeatures(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((item) => b.includes(item));
}
